/**
 * Cash Drawer integration via the WebUSB API.
 *
 * Hardware: Wodgam-style ESC/POS thermal receipt printer (e.g. WG8200, 80mm)
 * connected to the till computer over USB, with the cash drawer plugged into
 * the printer's RJ11/RJ12 "DK" port. Pressing the "Cash" pill in the app
 * needs to open that drawer.
 *
 * How: browsers can talk directly to USB devices via the WebUSB API
 * (Chrome/Edge on desktop). We claim the printer's bulk-OUT endpoint and
 * write the standard ESC/POS kick-out byte sequence:
 *
 *   0x1B 0x70 0x00 0x19 0xFA   // Open drawer 1 (pin 2)
 *
 * The first time the user clicks "Pair cash drawer" they get a one-time
 * Chrome permission prompt picking the printer. We store a local marker
 * so subsequent kicks reuse the same device without re-prompting.
 *
 * Notes:
 *   - WebUSB only works on HTTPS (✅ our preview + prod domains).
 *   - Safari does NOT support WebUSB — staff must use Chrome or Edge.
 *   - Some printers expose multiple interfaces; we walk through them and
 *     pick the first bulk OUT endpoint.
 *   - On a fresh OS install the printer might be claimed by a kernel driver
 *     (CUPS / Windows print spooler). On Windows you may need to install a
 *     Zadig WinUSB driver for the printer; on macOS pause CUPS or the kick
 *     can fail silently. The error toast tells the manager which step.
 */

const VENDOR_KEY = 'majnu:cashDrawer:paired';

// Standard ESC/POS drawer kick — drawer 1 / pin 2 / 25ms on / 250ms off.
// Works on Star, Epson, Bixolon, Wodgam and most generic 80mm thermals.
const KICK_BYTES = new Uint8Array([0x1B, 0x70, 0x00, 0x19, 0xFA]);

/** Pick the first bulk-OUT endpoint exposed by a USB printer device. */
function findBulkOutEndpoint(device) {
  for (const config of device.configurations || []) {
    for (const iface of config.interfaces || []) {
      for (const alt of iface.alternates || []) {
        // ESC/POS printers usually expose USB class 7 (printer) but some
        // use vendor-specific (0xFF), so don't filter strictly here.
        for (const ep of alt.endpoints || []) {
          if (ep.direction === 'out' && ep.type === 'bulk') {
            return { interfaceNumber: iface.interfaceNumber, alternate: alt.alternateSetting, endpoint: ep.endpointNumber };
          }
        }
      }
    }
  }
  return null;
}

/** Returns true if this browser supports WebUSB. */
export function isCashDrawerSupported() {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

/** Has the manager paired the drawer at least once on this device? */
export function isCashDrawerPaired() {
  if (!isCashDrawerSupported()) return false;
  try { return localStorage.getItem(VENDOR_KEY) === '1'; } catch { return false; }
}

/**
 * Pair the printer (one-time). Triggers the browser's USB picker so the
 * manager picks their thermal printer (e.g. "WG8200"). Saves a flag so we
 * skip the picker on subsequent kicks.
 */
export async function pairCashDrawer() {
  if (!isCashDrawerSupported()) {
    throw new Error('WebUSB not supported. Use Chrome or Edge on the till computer.');
  }
  // We don't restrict by vendorId/productId — let the manager pick whatever
  // their printer is from the picker. Filters could be tightened later.
  const device = await navigator.usb.requestDevice({ filters: [] });
  if (!device) throw new Error('No printer selected');
  try { localStorage.setItem(VENDOR_KEY, '1'); } catch { /* sandboxed */ }
  return { name: device.productName || 'Cash drawer printer' };
}

/**
 * Unpair / forget the cash drawer (revoke + clear marker).
 */
export async function forgetCashDrawer() {
  try { localStorage.removeItem(VENDOR_KEY); } catch { /* sandboxed */ }
  if (!isCashDrawerSupported()) return;
  try {
    const devices = await navigator.usb.getDevices();
    for (const d of devices) {
      try { await d.forget(); } catch { /* not all browsers expose forget() */ }
    }
  } catch (_e) { /* noop */ }
}

/**
 * Open the cash drawer by sending the ESC/POS kick command.
 * Resolves silently on success; rejects with a useful message on failure.
 */
export async function openCashDrawer() {
  if (!isCashDrawerSupported()) {
    throw new Error('WebUSB not supported in this browser. Use Chrome or Edge.');
  }
  let devices = [];
  try {
    devices = await navigator.usb.getDevices();
  } catch (e) {
    throw new Error(`Could not list USB devices: ${e.message}`);
  }
  if (!devices || devices.length === 0) {
    throw new Error('No paired printer. Tap "Pair cash drawer" first.');
  }

  // Try every paired device until one accepts the kick (helps if the
  // manager paired multiple printers historically).
  let lastErr;
  for (const device of devices) {
    try {
      if (!device.opened) await device.open();
      if (device.configuration === null && device.configurations.length > 0) {
        await device.selectConfiguration(device.configurations[0].configurationNumber);
      }
      const ep = findBulkOutEndpoint(device);
      if (!ep) {
        lastErr = new Error(`${device.productName || 'Printer'} has no bulk-OUT endpoint`);
        continue;
      }
      try { await device.claimInterface(ep.interfaceNumber); }
      catch (e) {
        lastErr = new Error(`Could not claim printer interface (driver may be holding it). ${e.message}`);
        continue;
      }
      try {
        if (ep.alternate !== 0) {
          await device.selectAlternateInterface(ep.interfaceNumber, ep.alternate);
        }
      } catch (_e) { /* some devices reject this — proceed anyway */ }
      const result = await device.transferOut(ep.endpoint, KICK_BYTES);
      try { await device.releaseInterface(ep.interfaceNumber); } catch { /* noop */ }
      if (result?.status === 'ok') return { device: device.productName || 'Printer' };
      lastErr = new Error(`USB transfer status ${result?.status}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Drawer kick failed');
}
